/**
 * Super Admin — Demo Operations.
 *
 * One-page control centre for the RestroPulse public demo:
 *   · status hero
 *   · share the demo (canonical URL + QR + copy)
 *   · campaign UTM builder
 *   · demo health cards
 *   · destructive actions (reset, end sessions)
 *
 * All actions call existing backend endpoints under `/api/super/demo/*`.
 * The reset action is gated behind a typed-confirmation modal.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Rocket, Copy, ExternalLink, Download, Link2, QrCode, ShieldCheck,
  ShieldAlert, AlertTriangle, RefreshCw, PowerOff, Users, Radio,
  Building2, Mail,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const PUBLIC_DEMO_URL = 'https://restropulse.online/demo';

const btnPrimary =
  'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md ' +
  'bg-[#143E30] hover:bg-[#1B5A45] text-white text-[13px] font-medium ' +
  'shadow-sm focus:outline-none focus:ring-2 focus:ring-[#143E30]/30 ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';
const btnSecondary =
  'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md ' +
  'bg-white border border-[#143E30]/40 text-[#143E30] hover:bg-[#E7EEEB] ' +
  'text-[13px] font-medium focus:outline-none focus:ring-2 focus:ring-[#143E30]/30';
const btnGhostRed =
  'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md ' +
  'border border-[#FEE2E2] text-[#B91C1C] hover:bg-[#FEF2F2] text-[13px] font-medium';

const STATUS_BADGE = {
  live:                    { text: 'LIVE',                    style: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: Rocket },
  configuration_required:  { text: 'CONFIGURATION REQUIRED',  style: 'bg-amber-100 text-amber-800 border-amber-200',       Icon: AlertTriangle },
  error:                   { text: 'ERROR',                   style: 'bg-red-100 text-red-800 border-red-200',             Icon: ShieldAlert },
};


export default function SuperAdminDemoOps() {
  const { API, getHeaders } = useAuth();
  const [health, setHealth]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [resetting, setReset]   = useState(false);
  const [endingSess, setEndS]   = useState(false);
  const [showReset, setShowRs]  = useState(false);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/super/demo/status`,
                                    { headers: getHeaders() });
      setHealth(r.data);
    } catch (e) {
      toast.error('Failed to load demo status');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => { loadHealth(); }, [loadHealth]);

  const runReset = async () => {
    setReset(true);
    try {
      const r = await axios.post(`${API}/super/demo/reset`, {},
                                    { headers: getHeaders() });
      toast.success('Demo reset successfully.');
      setShowRs(false);
      await loadHealth();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Reset failed.';
      toast.error(msg);
    } finally {
      setReset(false);
    }
  };

  const runEndSessions = async () => {
    if (!window.__demoOpsConfirmEnd) {
      window.__demoOpsConfirmEnd = true;
      toast('Click again to confirm — this will sign out every active demo visitor.', {
        duration: 4000,
      });
      setTimeout(() => { window.__demoOpsConfirmEnd = false; }, 4000);
      return;
    }
    window.__demoOpsConfirmEnd = false;
    setEndS(true);
    try {
      const r = await axios.post(`${API}/super/demo/sessions/end`, {},
                                    { headers: getHeaders() });
      toast.success(`Ended ${r.data?.invalidated || 0} active demo session(s).`);
      await loadHealth();
    } catch (e) {
      toast.error('Could not end sessions.');
    } finally {
      setEndS(false);
    }
  };

  return (
    <div data-testid="super-demo-ops-page">
      {/* ── Page header ────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold mb-1">
            RestroPulse
          </div>
          <h1 className="text-[26px] sm:text-[30px] font-semibold text-[#0A0F0D] tracking-tight leading-tight"
              style={{ fontFamily: 'Cabinet Grotesk, Figtree, ui-sans-serif' }}>
            Demo Operations
          </h1>
          <p className="text-[14px] text-[#5C6B64] mt-1.5">
            Share the public demo, monitor health, and reset the Amber sample dataset safely.
          </p>
        </div>
        <button onClick={loadHealth} disabled={loading} className={btnSecondary}
                data-testid="demo-ops-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.75}/>
          Refresh
        </button>
      </div>

      {/* ── Status hero ────────────────────────────────────────── */}
      <StatusHero health={health} />

      {/* ── Share demo + QR ────────────────────────────────────── */}
      <div className="mt-8 grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <ShareDemoCard />
        <CampaignBuilderCard />
      </div>

      {/* ── Health cards ───────────────────────────────────────── */}
      <HealthGrid health={health} />

      {/* ── Danger zone ────────────────────────────────────────── */}
      <Card className="mt-8 border-[#FCA5A5]/60">
        <CardContent className="p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#B91C1C] font-semibold">
            Danger zone
          </div>
          <div className="mt-2 flex items-start justify-between gap-6 flex-wrap">
            <div className="max-w-[540px]">
              <h3 className="text-[16px] font-semibold text-[#0A0F0D] tracking-tight">
                Reset Amber demo data
              </h3>
              <p className="text-[13px] text-[#5C6B64] mt-1">
                Restores The Amber Kitchen Group to the canonical demonstration dataset.
                Prospect leads, audit trail, and other tenants are never touched.
              </p>
            </div>
            <button className={btnGhostRed} onClick={() => setShowRs(true)}
                    data-testid="demo-ops-reset-open">
              <RefreshCw className="w-4 h-4" strokeWidth={1.75}/>
              Reset Amber Demo Data
            </button>
          </div>
          <div className="mt-6 border-t border-[#FEE2E2] pt-4 flex items-start justify-between gap-6 flex-wrap">
            <div className="max-w-[540px]">
              <h3 className="text-[15px] font-semibold text-[#0A0F0D] tracking-tight">
                End active demo sessions
              </h3>
              <p className="text-[13px] text-[#5C6B64] mt-1">
                Signs out every currently-active public demo visitor. Leads are preserved.
              </p>
            </div>
            <button className={btnGhostRed} onClick={runEndSessions} disabled={endingSess}
                    data-testid="demo-ops-end-sessions">
              <PowerOff className="w-4 h-4" strokeWidth={1.75}/>
              {endingSess ? 'Ending…' : 'End Active Demo Sessions'}
            </button>
          </div>
        </CardContent>
      </Card>

      {showReset && (
        <ResetConfirmModal onClose={() => !resetting && setShowRs(false)}
                            onConfirm={runReset} running={resetting} />
      )}
    </div>
  );
}


/* ── Status hero ──────────────────────────────────────────────── */
function StatusHero({ health }) {
  const s = STATUS_BADGE[health?.status] || STATUS_BADGE.configuration_required;
  const Icon = s.Icon;
  return (
    <Card className="border-[#E7EEEB] overflow-hidden" data-testid="demo-ops-status">
      <CardContent className="p-0">
        <div className="p-6 sm:p-8 bg-white flex items-start gap-6 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold">
              Public Demo
            </div>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <Badge className={`${s.style} border font-semibold uppercase tracking-widest text-[10.5px] px-2 py-0.5`}>
                <Icon className="w-3 h-3 mr-1" strokeWidth={2.25}/>{s.text}
              </Badge>
              <a href={PUBLIC_DEMO_URL} target="_blank" rel="noopener noreferrer"
                 className="text-[14px] font-mono text-[#143E30] hover:underline">
                {PUBLIC_DEMO_URL}
              </a>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3 max-w-[720px] text-[13px]">
              <HeroField label="Tenant"
                          value={health?.tenant?.name || 'The Amber Kitchen Group'}/>
              <HeroField label="Tenant ID" value={health?.tenant_id || 'amber_kitchen_group'} mono/>
              <HeroField label="Outlets"
                          value={`${health?.outlets?.count ?? '—'} of ${health?.outlets?.expected ?? 5}`}/>
              <HeroField label="Demo personas" value="Owner · Manager · Guest"/>
              <HeroField label="Leads captured"
                          value={String(health?.leads_total ?? '—')}/>
              <HeroField label="Active sessions"
                          value={String(health?.active_sessions ?? '—')}/>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function HeroField({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6B64] font-semibold">{label}</div>
      <div className={`mt-0.5 text-[14px] text-[#0A0F0D] ${mono ? 'font-mono' : ''}`}>
        {value}
      </div>
    </div>
  );
}


/* ── Share demo card ───────────────────────────────────────────── */
function ShareDemoCard() {
  const qrRef = useRef(null);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied');
    } catch { toast.error('Clipboard not available'); }
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'restropulse-demo-qr.png';
    a.click();
  };

  return (
    <Card className="border-[#E7EEEB]" data-testid="demo-ops-share">
      <CardContent className="p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold">
          Share the demo
        </div>
        <h3 className="mt-1 text-[17px] font-semibold text-[#0A0F0D] tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, Figtree, ui-sans-serif' }}>
          Canonical public URL
        </h3>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Input readOnly value={PUBLIC_DEMO_URL}
                  data-testid="demo-ops-share-url"
                  className="flex-1 min-w-[220px] max-w-[420px] h-9 font-mono text-[13px] text-[#143E30] bg-[#F4F7F5] border-[#E7EEEB]"/>
          <button className={btnSecondary}
                  onClick={() => copy(PUBLIC_DEMO_URL)}
                  data-testid="demo-ops-copy">
            <Copy className="w-3.5 h-3.5" strokeWidth={1.75}/> Copy Link
          </button>
          <a className={btnPrimary} href={PUBLIC_DEMO_URL}
             target="_blank" rel="noopener noreferrer"
             data-testid="demo-ops-open">
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75}/> Open Demo
          </a>
        </div>

        <div className="mt-5 flex items-start gap-6 flex-wrap">
          <div ref={qrRef}
               className="p-3 bg-white border border-[#E7EEEB] rounded-md shrink-0"
               data-testid="demo-ops-qr">
            <QRCodeCanvas value={PUBLIC_DEMO_URL} size={160}
                            fgColor="#143E30" bgColor="#FFFFFF"
                            level="M" includeMargin={false} />
          </div>
          <div className="flex-1 min-w-[220px] max-w-[380px] text-[13px] text-[#5C6B64] leading-relaxed">
            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#143E30] mb-2">
              <QrCode className="w-3 h-3" strokeWidth={2}/> QR
            </div>
            <p>
              Prints or projects cleanly. Scans open the canonical demo URL exactly —
              no redirect or wrapper domain.
            </p>
            <div className="mt-3">
              <button className={btnPrimary} onClick={downloadQr}
                      data-testid="demo-ops-qr-download">
                <Download className="w-3.5 h-3.5" strokeWidth={1.75}/> Download PNG
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


/* ── Campaign builder card ─────────────────────────────────────── */
function CampaignBuilderCard() {
  const [source,   setSource]   = useState('');
  const [medium,   setMedium]   = useState('');
  const [campaign, setCamp]     = useState('');
  const [content,  setContent]  = useState('');
  const [term,     setTerm]     = useState('');

  const url = useMemo(() => {
    const p = new URLSearchParams();
    if (source)   p.append('utm_source',   source);
    if (medium)   p.append('utm_medium',   medium);
    if (campaign) p.append('utm_campaign', campaign);
    if (content)  p.append('utm_content',  content);
    if (term)     p.append('utm_term',     term);
    const qs = p.toString();
    return qs ? `${PUBLIC_DEMO_URL}?${qs}` : PUBLIC_DEMO_URL;
  }, [source, medium, campaign, content, term]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Campaign link copied');
    } catch { toast.error('Clipboard not available'); }
  };

  const qrRef = useRef(null);
  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `restropulse-demo-${campaign || 'campaign'}-qr.png`;
    a.click();
  };

  const F = ({ label, value, setValue, testId, placeholder }) => (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C6B64] font-semibold">
        {label}
      </label>
      <Input value={value} onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder} className="h-9 mt-1"
              data-testid={testId}/>
    </div>
  );

  return (
    <Card className="border-[#E7EEEB]" data-testid="demo-ops-campaign">
      <CardContent className="p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold">
          Campaign links
        </div>
        <h3 className="mt-1 text-[17px] font-semibold text-[#0A0F0D] tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, Figtree, ui-sans-serif' }}>
          UTM builder
        </h3>
        <p className="mt-1 text-[13px] text-[#5C6B64]">
          Build a trackable /demo link. The public demo already captures every UTM
          into <code className="font-mono text-[12px] bg-[#F4F7F5] px-1 rounded">demo_access_leads</code>.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <F label="Source"   value={source}   setValue={setSource}   placeholder="google"          testId="utm-source"/>
          <F label="Medium"   value={medium}   setValue={setMedium}   placeholder="cpc"             testId="utm-medium"/>
          <F label="Campaign" value={campaign} setValue={setCamp}     placeholder="launch_q1"       testId="utm-campaign"/>
          <F label="Content"  value={content}  setValue={setContent}  placeholder="hero_button"     testId="utm-content"/>
          <F label="Term"     value={term}     setValue={setTerm}     placeholder="restaurant_saas" testId="utm-term"/>
        </div>

        <div className="mt-5">
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C6B64] font-semibold">
            Preview
          </label>
          <Input readOnly value={url}
                  data-testid="utm-preview"
                  className="mt-1 h-9 font-mono text-[12px] text-[#143E30] bg-[#F4F7F5] border-[#E7EEEB]"/>
        </div>

        <div className="mt-4 flex items-start gap-4 flex-wrap">
          <div ref={qrRef}
               className="p-2 bg-white border border-[#E7EEEB] rounded-md shrink-0"
               data-testid="campaign-qr">
            <QRCodeCanvas value={url} size={110}
                            fgColor="#143E30" bgColor="#FFFFFF"
                            level="M" includeMargin={false} />
          </div>
          <div className="flex-1 min-w-[220px] flex items-center gap-2 flex-wrap">
            <button className={btnSecondary} onClick={copy}
                    data-testid="utm-copy">
              <Copy className="w-3.5 h-3.5" strokeWidth={1.75}/> Copy Campaign Link
            </button>
            <button className={btnPrimary} onClick={downloadQr}
                    data-testid="utm-qr-download">
              <Download className="w-3.5 h-3.5" strokeWidth={1.75}/> Download QR
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


/* ── Health cards ──────────────────────────────────────────────── */
function HealthGrid({ health }) {
  const cards = [
    {
      label: 'Amber tenant',
      ready: health?.tenant?.is_demo_tenant === true,
      readyText: 'Ready', issueText: 'Missing',
      Icon: Building2,
      testId: 'health-tenant',
    },
    {
      label: 'Outlets',
      ready: !!health?.outlets?.ready,
      readyText: `${health?.outlets?.count ?? '—'} of ${health?.outlets?.expected ?? 5}`,
      issueText: `${health?.outlets?.count ?? '—'} of ${health?.outlets?.expected ?? 5}`,
      Icon: Building2,
      testId: 'health-outlets',
    },
    {
      label: 'Owner persona',
      ready: !!health?.owner?.ready,
      readyText: 'Ready', issueText: 'Missing',
      Icon: Users,
      testId: 'health-owner',
    },
    {
      label: 'Manager persona',
      ready: !!health?.manager?.ready,
      readyText: 'Ready', issueText: 'Missing',
      Icon: Users,
      testId: 'health-manager',
    },
    {
      label: 'Demo leads',
      neutral: true,
      value: health?.leads_total ?? '—',
      Icon: Users,
      testId: 'health-leads',
    },
    {
      label: 'Active demo sessions',
      neutral: true,
      value: health?.active_sessions ?? '—',
      Icon: Radio,
      testId: 'health-sessions',
    },
    {
      label: 'Turnstile',
      ready: health?.turnstile === 'configured',
      readyText: 'Configured',
      issueText: health?.turnstile === 'test_key' ? 'Test key' : 'Missing',
      Icon: ShieldCheck,
      testId: 'health-turnstile',
    },
    {
      label: 'Email delivery',
      ready: health?.email_delivery === 'configured',
      readyText: 'Configured', issueText: 'Not configured',
      Icon: Mail,
      testId: 'health-email',
    },
  ];

  return (
    <div className="mt-8">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold mb-3">
        Health
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => {
          const Icon = c.Icon;
          const bad = !c.neutral && !c.ready;
          return (
            <Card key={c.label} className="border-[#E7EEEB]"
                    data-testid={c.testId}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6B64] font-semibold">
                    {c.label}
                  </div>
                  <Icon className={`w-4 h-4 ${bad ? 'text-[#B91C1C]' : 'text-[#143E30]'}`}
                          strokeWidth={1.75}/>
                </div>
                <div className={`mt-2 text-[15px] font-semibold ${bad ? 'text-[#B91C1C]' : 'text-[#0A0F0D]'}`}>
                  {c.neutral ? c.value : (c.ready ? c.readyText : c.issueText)}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}


/* ── Reset confirmation modal ──────────────────────────────────── */
function ResetConfirmModal({ onClose, onConfirm, running }) {
  const [text, setText] = useState('');
  const armed = text.trim() === 'RESET DEMO';
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose}
           data-testid="demo-ops-reset-scrim"/>
      <div role="dialog" aria-modal="true"
           data-testid="demo-ops-reset-modal"
           className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                       w-[min(520px,calc(100vw-24px))] bg-white rounded-lg shadow-xl
                       border border-[#E7EEEB]">
        <div className="p-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#B91C1C] font-semibold">
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2}/> Destructive action
          </div>
          <h2 className="mt-1 text-[20px] font-semibold text-[#0A0F0D] tracking-tight"
              style={{ fontFamily: 'Cabinet Grotesk, Figtree, ui-sans-serif' }}>
            Reset Amber Demo?
          </h2>
          <p className="mt-2 text-[14px] text-[#3A4541] leading-relaxed">
            This restores The Amber Kitchen Group to the canonical demonstration dataset.
            Demo leads and prospect history will <strong>not</strong> be deleted; other tenants are untouched.
          </p>
          <div className="mt-4">
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C6B64] font-semibold">
              Type <code className="bg-[#F4F7F5] px-1.5 py-0.5 rounded font-mono text-[12px]">RESET DEMO</code> to confirm
            </label>
            <Input value={text} onChange={(e) => setText(e.target.value)}
                    autoFocus className="mt-1.5 h-10 font-mono"
                    data-testid="demo-ops-reset-typed"/>
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button className={btnSecondary} onClick={onClose} disabled={running}
                    data-testid="demo-ops-reset-cancel">
              Cancel
            </button>
            <button className={btnGhostRed}
                    style={{ background: armed ? '#B91C1C' : undefined,
                             color: armed ? '#fff' : undefined,
                             borderColor: armed ? '#B91C1C' : undefined }}
                    disabled={!armed || running}
                    onClick={onConfirm}
                    data-testid="demo-ops-reset-confirm">
              <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} strokeWidth={1.75}/>
              {running ? 'Resetting…' : 'Reset Demo'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
